/**
 * Before After Image Slider Carousel
 *
 * Handles slider initialization and re-calculation when carousel slides change.
 *
 * @package wp-before-after-image-slider
 * @since 2.1.0
 */

(function () {
	'use strict';

	/**
	 * Get original image dimensions from HTML attributes or naturalWidth
	 * Used to calculate aspect ratio
	 */
	function getOriginalImageDimensions(img) {
		// First try width/height attributes (from HTML) - always available immediately
		const attrWidth = parseInt(img.getAttribute('width'), 10);
		const attrHeight = parseInt(img.getAttribute('height'), 10);
		if (attrWidth > 0 && attrHeight > 0) {
			return { width: attrWidth, height: attrHeight };
		}
		// Fall back to naturalWidth (actual loaded image size)
		if (img.naturalWidth > 0 && img.naturalHeight > 0) {
			return { width: img.naturalWidth, height: img.naturalHeight };
		}
		return null;
	}

	/**
	 * Get original video dimensions from HTML attributes or videoWidth
	 * Used to calculate aspect ratio for video sliders
	 */
	function getOriginalVideoDimensions(video) {
		// First try width/height attributes (from HTML) - always available immediately
		// const attrWidth = parseInt(video.getAttribute('width'), 10);
		// const attrHeight = parseInt(video.getAttribute('height'), 10);
		// if (attrWidth > 0 && attrHeight > 0) {
		// 	return { width: attrWidth, height: attrHeight };
		// }
		// // Fall back to videoWidth (actual loaded video size)
		// if (video.videoWidth > 0 && video.videoHeight > 0) {
		// 	return { width: video.videoWidth, height: video.videoHeight };
		// }
		// Default 16:9 aspect ratio for videos
		return { width: 640, height: 360 };
	}

	/**
	 * Get original iframe dimensions from HTML attributes
	 * Used to calculate aspect ratio for YouTube video sliders
	 */
	function getOriginalIframeDimensions(iframe) {
		// Try width/height attributes (from HTML)
		const attrWidth = parseInt(iframe.getAttribute('width'), 10);
		const attrHeight = parseInt(iframe.getAttribute('height'), 10);
		if (attrWidth > 0 && attrHeight > 0) {
			return { width: attrWidth, height: attrHeight };
		}
		// Default 16:9 aspect ratio for YouTube
		return { width: 640, height: 360 };
	}

	/**
	 * Get the actual rendered dimensions for a slider inside a carousel slide
	 * This accounts for slides-per-view constraints
	 */
	function getRenderedDimensions(sliderElement, originalDims) {
		// Find the parent swiper-slide to get the actual available width
		const swiperSlide = sliderElement.closest('swiper-slide');
		if (!swiperSlide) {
			// Not in a carousel, use original dimensions
			return originalDims;
		}

		// Get the swiper slide's actual rendered width
		const slideWidth = swiperSlide.getBoundingClientRect().width;
		if (slideWidth <= 0) {
			return originalDims;
		}

		// Account for padding in the slide wrapper
		const slideWrapper = sliderElement.closest('.bais-carousel-slide');
		let availableWidth = slideWidth;
		if (slideWrapper) {
			const wrapperStyle = window.getComputedStyle(slideWrapper);
			const paddingLeft = parseFloat(wrapperStyle.paddingLeft) || 0;
			const paddingRight = parseFloat(wrapperStyle.paddingRight) || 0;
			availableWidth = slideWidth - paddingLeft - paddingRight;
		}

		// If original image fits within available width, use original dimensions
		if (originalDims.width <= availableWidth) {
			return originalDims;
		}

		// Calculate scaled dimensions maintaining aspect ratio
		const aspectRatio = originalDims.width / originalDims.height;
		const scaledWidth = Math.floor(availableWidth);
		const scaledHeight = Math.floor(scaledWidth / aspectRatio);

		return { width: scaledWidth, height: scaledHeight };
	}

	/**
	 * Sync the max-width of any sibling .coca-bais-caption-wrapper to match the slider container.
	 *
	 * @param {HTMLElement} sliderElement  The .coca-bais-container DOM element.
	 * @param {number}      width          The container width in pixels.
	 */
	function syncCaptionWrapperWidth(sliderElement, width) {
		const sc = sliderElement.closest('.shortcode-container');
		if (sc) {
			const cw = sc.querySelector('.coca-bais-caption-wrapper');
			if (cw) {
				cw.style.maxWidth = width + 'px';
			}
		}
	}

	/**
	 * Apply correct dimensions to a slider container and its images
	 * Uses rendered dimensions that account for slides-per-view
	 */
	function applySliderDimensions(sliderElement, images, originalDims, settings) {
		// Get actual rendered dimensions based on available space
		const dims = getRenderedDimensions(sliderElement, originalDims);
		const width = dims.width;
		const height = dims.height;
		const defaultOffset = settings.default_offset_pct || 0.5;
		const handlePos = Math.round(width * defaultOffset);

		// Fix container dimensions - use rendered size, not original
		sliderElement.style.maxWidth = width + 'px';
		sliderElement.style.width = width + 'px';
		sliderElement.style.height = height + 'px';
		sliderElement.style.minHeight = height + 'px';
		// Clear aspect-ratio to prevent it from overriding explicit height
		sliderElement.style.aspectRatio = '';
		
		// Fix ALL images dimensions to match container
		images.forEach(function(img) {
			img.style.width = width + 'px';
		});

		// Fix handle position based on rendered width
		const handle = sliderElement.querySelector('.coca-image-compare-handle');
		if (handle) {
			handle.style.left = handlePos + 'px';
		}

		// Fix clip rects for before/after images using rendered dimensions
		const beforeImg = sliderElement.querySelector('.coca-image-compare-before');
		const afterImg = sliderElement.querySelector('.coca-image-compare-after');
		
		if (beforeImg) {
			beforeImg.style.clip = 'rect(0px, ' + handlePos + 'px, ' + height + 'px, 0px)';
		}
		if (afterImg) {
			afterImg.style.clip = 'rect(0px, ' + width + 'px, ' + height + 'px, ' + handlePos + 'px)';
		}

		// Mark as carousel-fixed to prevent plugin from overwriting
		sliderElement.setAttribute('data-carousel-fixed', 'true');
		// Store the rendered dimensions for reference
		sliderElement.setAttribute('data-carousel-width', width);
		sliderElement.setAttribute('data-carousel-height', height);
		syncCaptionWrapperWidth(sliderElement, width);
	}

	/**
	 * Apply correct dimensions to a video slider container
	 * Uses rendered dimensions that account for slides-per-view
	 * Also initializes clips if they're at 0 (slider wasn't initialized in hidden slide)
	 */
	function applyVideoSliderDimensions(sliderElement, videos, originalDims, settings) {
		// Get actual rendered dimensions based on available space
		const dims = getRenderedDimensions(sliderElement, originalDims);
		const width = dims.width;
		const height = dims.height;
		const defaultOffset = settings.default_offset_pct || 0.5;
		const handlePos = Math.round(width * defaultOffset);

		// Fix container dimensions - use rendered size
		sliderElement.style.maxWidth = width + 'px';
		sliderElement.style.width = width + 'px';
		sliderElement.style.height = height + 'px';
		sliderElement.style.minHeight = height + 'px';
		// Clear aspect-ratio to prevent it from overriding explicit height
		sliderElement.style.aspectRatio = '';
		sliderElement.style.opacity = '1';

		// Fix video wrapper dimensions (use setProperty with important to override CSS !important)
		const videoWrapper = sliderElement.querySelector('.coca-video-compare-wrapper');
		if (videoWrapper) {
			videoWrapper.style.width = width + 'px';
			videoWrapper.style.setProperty('height', height + 'px', 'important');
			videoWrapper.style.paddingBottom = '0';
		}

		// Check if clips need initialization (they're at 0 when slider was in hidden slide during init)
		const beforeVideo = sliderElement.querySelector('.coca-image-compare-before');
		const afterVideo = sliderElement.querySelector('.coca-image-compare-after');
		const beforeParent = beforeVideo ? beforeVideo.parentElement : null;
		
		// Check if before parent clip has 0 width (pattern: rect(0px, 0px, ...))
		let needsClipInit = false;
		if (beforeParent && beforeParent.style.clip) {
			const clipMatch = beforeParent.style.clip.match(/rect\s*\(\s*\d+px\s*,\s*(\d+)px/);
			if (clipMatch && parseInt(clipMatch[1], 10) === 0) {
				needsClipInit = true;
			}
		}

		// Fix all videos dimensions to match container
		videos.forEach(function(video) {
			video.style.width = width + 'px';
			video.style.height = height + 'px';
			video.style.maxWidth = width + 'px';
			// Also update parent div if it exists
			if (video.parentElement && video.parentElement.tagName === 'DIV') {
				video.parentElement.style.width = width + 'px';
				video.parentElement.style.height = height + 'px';
			}
		});

		// Initialize clips if they were never set (slider was in hidden carousel slide)
		if (needsClipInit) {
			// Set handle position
			const handle = sliderElement.querySelector('.coca-image-compare-handle');
			if (handle) {
				handle.style.left = handlePos + 'px';
			}

			// Set clips for before video and its parent
			if (beforeVideo) {
				beforeVideo.style.clip = 'rect(0px, ' + handlePos + 'px, ' + height + 'px, 0px)';
				if (beforeParent && beforeParent.tagName === 'DIV') {
					beforeParent.style.clip = 'rect(0px, ' + handlePos + 'px, ' + height + 'px, 0px)';
				}
			}

			// Set clips for after parent (after video itself doesn't get clipped to prevent shaking)
			if (afterVideo) {
				const afterParent = afterVideo.parentElement;
				if (afterParent && afterParent.tagName === 'DIV') {
					afterParent.style.clip = 'rect(0px, ' + width + 'px, ' + height + 'px, ' + handlePos + 'px)';
				}
			}
		}

		// Mark as carousel-fixed to prevent plugin from overwriting
		sliderElement.setAttribute('data-carousel-fixed', 'true');
		// Store the rendered dimensions for reference
		sliderElement.setAttribute('data-carousel-width', width);
		sliderElement.setAttribute('data-carousel-height', height);
		syncCaptionWrapperWidth(sliderElement, width);
	}

	/**
	 * Apply correct dimensions to an iframe (YouTube) slider container
	 * Uses rendered dimensions that account for slides-per-view
	 * Also initializes clips if they're at 0 (slider wasn't initialized in hidden slide)
	 */
	function applyIframeSliderDimensions(sliderElement, iframes, originalDims, settings) {
		// Get actual rendered dimensions based on available space
		const dims = getRenderedDimensions(sliderElement, originalDims);
		const width = dims.width;
		const height = dims.height;
		const defaultOffset = settings.default_offset_pct || 0.5;
		const handlePos = Math.round(width * defaultOffset);

		// Fix container dimensions - use rendered size
		sliderElement.style.maxWidth = width + 'px';
		sliderElement.style.width = width + 'px';
		sliderElement.style.height = height + 'px';
		sliderElement.style.minHeight = height + 'px';
		// Clear aspect-ratio to prevent it from overriding explicit height
		sliderElement.style.aspectRatio = '';
		sliderElement.style.opacity = '1';

		// Fix video wrapper dimensions (use setProperty with important to override CSS !important)
		const videoWrapper = sliderElement.querySelector('.coca-video-compare-wrapper');
		if (videoWrapper) {
			videoWrapper.style.width = width + 'px';
			videoWrapper.style.setProperty('height', height + 'px', 'important');
			videoWrapper.style.paddingBottom = '0';
		}

		// Check if clips need initialization (they're at 0 when slider was in hidden slide during init)
		const beforeIframe = sliderElement.querySelector('.coca-image-compare-before');
		const afterIframe = sliderElement.querySelector('.coca-image-compare-after');
		const beforeParent = beforeIframe ? beforeIframe.parentElement : null;
		
		// Check if before parent clip has 0 width (pattern: rect(0px, 0px, ...))
		let needsClipInit = false;
		if (beforeParent && beforeParent.style.clip) {
			const clipMatch = beforeParent.style.clip.match(/rect\s*\(\s*\d+px\s*,\s*(\d+)px/);
			if (clipMatch && parseInt(clipMatch[1], 10) === 0) {
				needsClipInit = true;
			}
		}

		// Fix all iframes dimensions to match container
		iframes.forEach(function(iframe) {
			iframe.style.width = width + 'px';
			iframe.style.height = height + 'px';
			// Also update parent div if it exists
			if (iframe.parentElement && iframe.parentElement.tagName === 'DIV') {
				iframe.parentElement.style.width = width + 'px';
				iframe.parentElement.style.height = height + 'px';
			}
		});

		// Initialize clips if they were never set (slider was in hidden carousel slide)
		if (needsClipInit) {
			// Set handle position
			const handle = sliderElement.querySelector('.coca-image-compare-handle');
			if (handle) {
				handle.style.left = handlePos + 'px';
			}

			// Set clips for before iframe and its parent
			if (beforeIframe) {
				beforeIframe.style.clip = 'rect(0px, ' + handlePos + 'px, ' + height + 'px, 0px)';
				if (beforeParent && beforeParent.tagName === 'DIV') {
					beforeParent.style.clip = 'rect(0px, ' + handlePos + 'px, ' + height + 'px, 0px)';
				}
			}

			// Set clips for after parent (after iframe itself doesn't get clipped to prevent shaking)
			if (afterIframe) {
				const afterParent = afterIframe.parentElement;
				if (afterParent && afterParent.tagName === 'DIV') {
					afterParent.style.clip = 'rect(0px, ' + width + 'px, ' + height + 'px, ' + handlePos + 'px)';
				}
			}
		}

		// Mark as carousel-fixed to prevent plugin from overwriting
		sliderElement.setAttribute('data-carousel-fixed', 'true');
		// Store the rendered dimensions for reference
		sliderElement.setAttribute('data-carousel-width', width);
		sliderElement.setAttribute('data-carousel-height', height);
		syncCaptionWrapperWidth(sliderElement, width);
	}

	/**
	 * Re-initialize/recalculate slider dimensions when it becomes visible
	 * Uses actual rendered dimensions based on slide width
	 * @param {Element} sliderElement - The slider container element
	 * @param {boolean} forceReinit - Force reinit even if dimensions look correct
	 */
	function reinitializeSlider(sliderElement, forceReinit) {
		const $slider = window.jQuery ? window.jQuery(sliderElement) : null;
		if (!$slider) return;

		// Check if it's a default slider
		if (sliderElement.classList.contains('coca-image-compare-container')) {
			const settings = $slider.data('settings') || {};

			// Get all images
			const images = sliderElement.querySelectorAll('.coca-bais-image');
			
			// Check for video sliders (MP4) if no images found
			const videos = sliderElement.querySelectorAll('video');
			
			// Check for iframe sliders (YouTube) if no images or videos found
			const iframes = sliderElement.querySelectorAll('iframe');
			
			if (images.length === 0 && videos.length === 0 && iframes.length === 0) return;

			// Handle MP4 video slider
			// if (images.length === 0 && videos.length > 0) {
			// 	const originalDims = getOriginalVideoDimensions(videos[0]);
				
			// 	// Get expected rendered dimensions
			// 	const expectedDims = getRenderedDimensions(sliderElement, originalDims);
			// 	const currentWidth = parseInt(sliderElement.style.maxWidth, 10) || 0;

			// 	// Skip if already has correct rendered dimensions and not forced
			// 	if (!forceReinit && currentWidth === expectedDims.width) {
			// 		return;
			// 	}

			// 	// Apply dimensions for video slider
			// 	applyVideoSliderDimensions(sliderElement, videos, originalDims, settings);
			// 	return;
			// }

			// Handle YouTube iframe slider
			// if (images.length === 0 && videos.length === 0 && iframes.length > 0) {
			// 	const originalDims = getOriginalIframeDimensions(iframes[0]);
				
			// 	// Get expected rendered dimensions
			// 	const expectedDims = getRenderedDimensions(sliderElement, originalDims);
			// 	const currentWidth = parseInt(sliderElement.style.maxWidth, 10) || 0;

			// 	// Skip if already has correct rendered dimensions and not forced
			// 	if (!forceReinit && currentWidth === expectedDims.width) {
			// 		return;
			// 	}

			// 	// Apply dimensions for iframe slider
			// 	applyIframeSliderDimensions(sliderElement, iframes, originalDims, settings);
			// 	return;
			// }

			// Handle image slider
			// Get original dimensions from image attributes
			const originalDims = getOriginalImageDimensions(images[0]);
			if (!originalDims) {
				// Wait for first image to load if dimensions not available
				images[0].addEventListener('load', function() {
					const loadedDims = getOriginalImageDimensions(images[0]);
					if (loadedDims) {
						applySliderDimensions(sliderElement, images, loadedDims, settings);
					}
				}, { once: true });
				return;
			}

			// Get expected rendered dimensions
			const expectedDims = getRenderedDimensions(sliderElement, originalDims);
			const currentWidth = parseInt(sliderElement.style.width, 10) || 0;

			// Skip if already has correct rendered dimensions and not forced
			if (!forceReinit && currentWidth === expectedDims.width) {
				return;
			}

			// Apply dimensions based on rendered space
			applySliderDimensions(sliderElement, images, originalDims, settings);
		}
		
		// Check if it's a triple slider
		if (sliderElement.classList.contains('coca-image-compare-triple-container')) {
			const images = sliderElement.querySelectorAll('.coca-bais-image');
			if (images.length === 0) return;

			// Get original dimensions
			const originalDims = getOriginalImageDimensions(images[0]);
			if (!originalDims) {
				// Wait for first image to load
				images[0].addEventListener('load', function() {
					const loadedDims = getOriginalImageDimensions(images[0]);
					if (loadedDims) {
						applyTripleSliderDimensions(sliderElement, images, loadedDims, $slider);
					}
				}, { once: true });
				return;
			}

			// Get expected rendered dimensions
			const expectedDims = getRenderedDimensions(sliderElement, originalDims);
			const currentWidth = parseInt(sliderElement.style.width, 10) || 0;

			// Skip if already has correct rendered dimensions and not forced
			if (!forceReinit && currentWidth === expectedDims.width) {
				return;
			}

			// Apply dimensions based on rendered space
			applyTripleSliderDimensions(sliderElement, images, originalDims, $slider);
		}
	}

	/**
	 * Apply dimensions to triple slider and reinitialize
	 * Uses rendered dimensions that account for slides-per-view
	 */
	function applyTripleSliderDimensions(sliderElement, images, originalDims, $slider) {
		// Get actual rendered dimensions based on available space
		const dims = getRenderedDimensions(sliderElement, originalDims);
		const width = dims.width;
		const height = dims.height;

		// Set container dimensions BEFORE reinitializing
		sliderElement.setAttribute('data-max-width', width);
		sliderElement.setAttribute('data-height', height);
		sliderElement.style.maxWidth = width + 'px';
		sliderElement.style.width = width + 'px';
		sliderElement.style.height = height + 'px';
		sliderElement.style.minHeight = height + 'px';
		// Clear aspect-ratio to prevent it from overriding explicit height
		sliderElement.style.aspectRatio = '';
		
		// Mark as carousel-fixed
		sliderElement.setAttribute('data-carousel-fixed', 'true');
		sliderElement.setAttribute('data-carousel-width', width);
		sliderElement.setAttribute('data-carousel-height', height);
		syncCaptionWrapperWidth(sliderElement, width);
		
		// Set image dimensions to match rendered size
		images.forEach(function(img) {
			img.style.maxWidth = width + 'px';
			img.style.width = width + 'px';
		});

		// Fix clip rects and wrapper widths based on rendered dimensions
		const wrappers = sliderElement.querySelectorAll('.image-wrapper');
		wrappers.forEach(function(wrapper) {
			// Update clip rect height
			const currentClip = wrapper.style.clip;
			if (currentClip) {
				// Parse existing clip values and update height
				const clipMatch = currentClip.match(/rect\(([^,]+),\s*([^,]+),\s*([^,]+),\s*([^)]+)\)/);
				if (clipMatch) {
					const newClip = 'rect(0px, ' + width + 'px, ' + height + 'px, ' + clipMatch[4] + ')';
					wrapper.style.clip = newClip;
				}
			}
		});

		// Fix scroller positions based on rendered width
		const scrollers = sliderElement.querySelectorAll('.scroller');
		scrollers.forEach(function(scroller) {
			const currentLeft = parseFloat(scroller.style.left) || 0;
			// Scale the position proportionally
			if (originalDims.width > 0 && currentLeft > 0) {
				const scaleFactor = width / originalDims.width;
				const newLeft = Math.round(currentLeft * scaleFactor);
				scroller.style.left = newLeft + 'px';
			}
		});
		
		// Reinitialize the plugin with correct dimensions
		const settings = $slider.data('settings') || {};
		if (window.jQuery && window.jQuery.fn.imageCompareTriple) {
			sliderElement.classList.remove('initialized');
			sliderElement.classList.remove('coca-bais-loaded');
			
			setTimeout(function() {
				$slider.imageCompareTriple(settings);
			}, 50);
		}
	}

	/**
	 * Sync mute/unmute button UI with actual video muted state
	 */
	function syncMuteButtonState(swiperSlide) {
		// Find all HTML5 videos in this slide
		const videos = swiperSlide.querySelectorAll('video.coca-image-compare-before, video.coca-image-compare-after');
		if (videos.length > 0) {
			const muteButton = swiperSlide.querySelector('.video-sound');
			if (muteButton && muteButton.children.length === 2) {
				// Check the actual muted state from any video (they should be synced)
				const isMuted = videos[0].muted;
				
				if (isMuted) {
					// Show muted icon (first child)
					muteButton.children[0].classList.remove('hidden');
					muteButton.children[0].classList.add('show');
					// Hide unmuted icon (second child)
					muteButton.children[1].classList.remove('show');
					muteButton.children[1].classList.add('hidden');
				} else {
					// Hide muted icon (first child)
					muteButton.children[0].classList.remove('show');
					muteButton.children[0].classList.add('hidden');
					// Show unmuted icon (second child)
					muteButton.children[1].classList.remove('hidden');
					muteButton.children[1].classList.add('show');
				}
			}
		}
		
		// For YouTube iframes, we can't directly check mute state,
		// but the button state should have been preserved from pauseInactiveVideos
	}

	/**
	 * Initialize sliders in active carousel slide
	 */
	function initializeSlidersInSlide(swiperSlide) {
		// Find all sliders in this slide
		const sliders = swiperSlide.querySelectorAll('.coca-bais-container, .coca-bais-triple-container');
		
		sliders.forEach(function(slider) {
			// Wait for slide to be fully visible and slider plugin to finish
			setTimeout(function() {
				// Force reinit for carousel sliders to ensure correct dimensions
				reinitializeSlider(slider, true);
			}, 50);
		});
		
		// Sync the mute/unmute button state with actual video state
		syncMuteButtonState(swiperSlide);
	}

	/**
	 * Scan all carousel slides and fix any sliders with wrong dimensions
	 * Compares current dimensions against rendered dimensions (not original)
	 */
	function scanAndFixCarouselSliders() {
		const carousels = document.querySelectorAll('swiper-container.bais-carousel');
		
		carousels.forEach(function(carousel) {
			const slides = carousel.querySelectorAll('swiper-slide');
			slides.forEach(function(slide) {
				const sliders = slide.querySelectorAll('.coca-bais-container, .coca-bais-triple-container');
				sliders.forEach(function(slider) {
					const firstImg = slider.querySelector('.coca-bais-image');
					const originalDims = firstImg ? getOriginalImageDimensions(firstImg) : null;
					
					if (!originalDims) return;
					
					// Get expected rendered dimensions
					const expectedDims = getRenderedDimensions(slider, originalDims);
					const currentWidth = parseInt(slider.style.width, 10) || 0;
					
					// Fix if current width doesn't match expected rendered width
					if (currentWidth === 0 || currentWidth !== expectedDims.width) {
						reinitializeSlider(slider, true);
					}
				});
			});
		});
	}

	/**
	 * Watch for style changes on carousel sliders and fix wrong dimensions
	 * Uses rendered dimensions that account for slides-per-view
	 */
	function watchCarouselSliders() {
		const carousels = document.querySelectorAll('swiper-container.bais-carousel');
		
		carousels.forEach(function(carousel) {
			const sliders = carousel.querySelectorAll('.coca-bais-container, .coca-bais-triple-container');
			
			sliders.forEach(function(slider) {
				// Skip if already watching
				if (slider.hasAttribute('data-carousel-watching')) {
					return;
				}
				slider.setAttribute('data-carousel-watching', 'true');
				
				let debounceTimer = null;
				let isApplyingFix = false;
				
				// Watch for style attribute changes
				const observer = new MutationObserver(function(mutations) {
					// Skip if we're currently applying our own fix
					if (isApplyingFix) {
						return;
					}
					
					mutations.forEach(function(mutation) {
						if (mutation.attributeName === 'style') {
							const firstImg = slider.querySelector('.coca-bais-image');
							const originalDims = firstImg ? getOriginalImageDimensions(firstImg) : null;
							
							if (!originalDims) return;
							
							// Get expected rendered dimensions
							const expectedDims = getRenderedDimensions(slider, originalDims);
							const currentWidth = parseInt(slider.style.width, 10) || 0;
							const currentHeight = parseInt(slider.style.height, 10) || 0;
							
							// If dimensions don't match rendered expectations, or aspect-ratio is set, fix them
							const hasAspectRatio = slider.style.aspectRatio && slider.style.aspectRatio !== '';
							if (currentWidth !== expectedDims.width || currentHeight !== expectedDims.height || hasAspectRatio) {
								// Clear any pending fix
								if (debounceTimer) {
									clearTimeout(debounceTimer);
								}
								
								// Debounce to let the plugin finish, then fix
								debounceTimer = setTimeout(function() {
									isApplyingFix = true;
									const settings = window.jQuery ? window.jQuery(slider).data('settings') || {} : {};
									const images = slider.querySelectorAll('.coca-bais-image');
									applySliderDimensions(slider, images, originalDims, settings);
									
									// Allow future observations after a delay
									setTimeout(function() {
										isApplyingFix = false;
									}, 100);
								}, 50);
							}
						}
					});
				});
				
				observer.observe(slider, { attributes: true, attributeFilter: ['style'] });
			});
		});
	}

	/**
	 * Inject custom styles into Swiper Shadow DOM
	 * This allows us to style elements inside the shadow root that aren't exposed via ::part()
	 */
	function injectSwiperShadowStyles() {
		const carousels = document.querySelectorAll('swiper-container.bais-carousel');
		
		carousels.forEach(function(carousel) {
			// Skip if already injected
			if (carousel.hasAttribute('data-styles-injected')) {
				return;
			}
			
			// Wait for shadow root to be available
			if (carousel.shadowRoot) {
				const style = document.createElement('style');
				style.textContent = `
					.swiper {
						padding-bottom: 40px !important; /* Space for pagination */
					}
					.swiper-horizontal .swiper-button-next, .swiper-horizontal .swiper-button-prev, .swiper-horizontal~.swiper-button-next, .swiper-horizontal~.swiper-button-prev {
						top: calc(50% - 31px) !important; /* Move navigation buttons down to be centered with pagination */
					}
					/* Custom navigation icon sizing */
					.swiper-button-next svg,
					.swiper-button-prev svg {
						width: 50% !important;
						height: 50% !important;
					}
					
					/* Navigation button hover state */
					.swiper-button-next:hover,
					.swiper-button-prev:hover {
						--swiper-navigation-color: #fff;
					}
				`;
				carousel.shadowRoot.appendChild(style);
				carousel.setAttribute('data-styles-injected', 'true');
			}
		});
	}

	/**
	 * Pause videos and reset controls (both play/pause and mute) in all slides except the active one
	 */
	function pauseInactiveVideos(swiper) {
		if (!swiper || !swiper.slides) return;
		
		swiper.slides.forEach(function(slide, index) {
			// Skip the currently active slide
			if (index === swiper.activeIndex) return;
			
			// Find all HTML5 videos in this slide
			const videos = slide.querySelectorAll('video.coca-image-compare-before, video.coca-image-compare-after');
			videos.forEach(function(video) {
				if (!video.paused) {
					// Check if this is an auto-slide enabled slider
					const container = video.closest('.coca-image-compare-container');
					let isAutoSlide = false;
					if (container) {
						// Check data-settings JSON for auto_slide_enabled
						const settingsAttr = container.getAttribute('data-settings');
						if (settingsAttr) {
							try {
								const settings = JSON.parse(settingsAttr);
								isAutoSlide = settings.auto_slide_enabled === true;
							} catch (e) {
								// Fallback to checking direct attribute
								isAutoSlide = container.getAttribute('data-auto-slide-enabled') === 'true' ||
											  container.getAttribute('data-auto-slide-enabled') === '1';
							}
						}
					}
					
					// Pause and mute the video (reset to muted state)
					video.pause();
					video.muted = true;
					
					// Reset the play/pause button to show "play" icon
					// UNLESS it's an auto-slide video (which plays continuously)
					if (!isAutoSlide) {
						const playPauseContainer = slide.querySelector('.video-play-pause');
						if (playPauseContainer && playPauseContainer.children.length === 2) {
							// Show play icon (first child)
							playPauseContainer.children[0].classList.remove('hidden');
							playPauseContainer.children[0].classList.add('show');
							// Hide pause icon (second child)
							playPauseContainer.children[1].classList.remove('show');
							playPauseContainer.children[1].classList.add('hidden');
						}
					}
					// For auto-slide videos, keep showing "pause" button since they play non-stop
					
					// Reset the mute button to show "muted" icon
					const muteButton = slide.querySelector('.video-sound');
					if (muteButton && muteButton.children.length === 2) {
						// Show muted icon (first child)
						muteButton.children[0].classList.remove('hidden');
						muteButton.children[0].classList.add('show');
						// Hide unmuted icon (second child)
						muteButton.children[1].classList.remove('show');
						muteButton.children[1].classList.add('hidden');
					}
				}
			});
			
			// Find all YouTube iframes and pause/mute them
			const iframes = slide.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtu.be"]');
			iframes.forEach(function(iframe) {
				// Check if this is an auto-slide enabled slider
				const container = iframe.closest('.coca-image-compare-container');
				let isAutoSlide = false;
				if (container) {
					// Check data-settings JSON for auto_slide_enabled
					const settingsAttr = container.getAttribute('data-settings');
					if (settingsAttr) {
						try {
							const settings = JSON.parse(settingsAttr);
							isAutoSlide = settings.auto_slide_enabled === true;
						} catch (e) {
							// Fallback to checking direct attribute
							isAutoSlide = container.getAttribute('data-auto-slide-enabled') === 'true' ||
										  container.getAttribute('data-auto-slide-enabled') === '1';
						}
					}
				}
				
				if (iframe.contentWindow) {
					try {
						// Pause and mute the YouTube video
						iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
						iframe.contentWindow.postMessage('{"event":"command","func":"mute","args":""}', '*');
					} catch (e) {
						// Silent fail if iframe is not accessible
					}
				}
				
				// Reset the play/pause button to show "play" icon
				// UNLESS it's an auto-slide video (which plays continuously)
				if (!isAutoSlide) {
					const playPauseContainer = slide.querySelector('.video-play-pause');
					if (playPauseContainer && playPauseContainer.children.length === 2) {
						playPauseContainer.children[0].classList.remove('hidden');
						playPauseContainer.children[0].classList.add('show');
						playPauseContainer.children[1].classList.remove('show');
						playPauseContainer.children[1].classList.add('hidden');
					}
				}
				// For auto-slide videos, keep showing "pause" button since they play non-stop
				
				// Reset the mute button to show "muted" icon
				const muteButton = slide.querySelector('.video-sound');
				if (muteButton && muteButton.children.length === 2) {
					// Show muted icon (first child)
					muteButton.children[0].classList.remove('hidden');
					muteButton.children[0].classList.add('show');
					// Hide unmuted icon (second child)
					muteButton.children[1].classList.remove('show');
					muteButton.children[1].classList.add('hidden');
				}
			});
		});
	}

	/**
	 * Adjust slides per view when total slides < slides per view and loop is disabled
	 */
	function adjustSlidesPerView(swiper, carousel) {
		if (!swiper || !swiper.params) {
			return;
		}

		// Check if loop is disabled
		const isLoopDisabled = !swiper.params.loop;
		
		if (!isLoopDisabled) {
			return; // Don't adjust if loop is enabled
		}

		// Get total number of slides
		const totalSlides = swiper.slides ? swiper.slides.length : 0;
		
		if (totalSlides === 0) {
			return;
		}

		// Get current breakpoint's slidesPerView
		const currentBreakpoint = swiper.currentBreakpoint;
		let slidesPerView = swiper.params.slidesPerView;
		
		// If we have breakpoints, get the current one's slidesPerView
		if (currentBreakpoint && swiper.params.breakpoints && swiper.params.breakpoints[currentBreakpoint]) {
			slidesPerView = swiper.params.breakpoints[currentBreakpoint].slidesPerView || slidesPerView;
		}

		// If total slides < slidesPerView, adjust to use totalSlides
		if (totalSlides < slidesPerView) {
			// Update the params for current view
			swiper.params.slidesPerView = totalSlides;
			
			// Update breakpoints if they exist
			if (swiper.params.breakpoints) {
				Object.keys(swiper.params.breakpoints).forEach(function(breakpoint) {
					const bpSlidesPerView = swiper.params.breakpoints[breakpoint].slidesPerView;
					if (bpSlidesPerView && totalSlides < bpSlidesPerView) {
						swiper.params.breakpoints[breakpoint].slidesPerView = totalSlides;
					}
				});
			}
			
			// Update the swiper to apply changes
			swiper.update();
		}
	}

	/**
	 * Setup carousel slide change handlers
	 */
	function setupCarouselHandlers() {
		const carousels = document.querySelectorAll('swiper-container.bais-carousel');
		
		// Inject shadow DOM styles
		injectSwiperShadowStyles();
		
		carousels.forEach(function(carousel) {
			// Get the swiper instance from the web component
			carousel.addEventListener('swiperslidechange', function(event) {
				const swiper = event.detail[0];
				if (swiper && swiper.slides) {
					// Pause videos in inactive slides and reset their controls
					pauseInactiveVideos(swiper);
					
					const activeSlide = swiper.slides[swiper.activeIndex];
					if (activeSlide) {
						initializeSlidersInSlide(activeSlide);
					}
				}
			});

			// Also initialize on first load
			carousel.addEventListener('swiperinit', function(event) {
				// Inject styles once swiper is initialized (shadow root ready)
				injectSwiperShadowStyles();
				
				const swiper = event.detail[0];
				if (swiper && swiper.slides) {
					// Adjust slides per view if needed
					adjustSlidesPerView(swiper, carousel);
					
					// Initialize ALL slides to ensure correct dimensions
					swiper.slides.forEach(function(slide) {
						initializeSlidersInSlide(slide);
					});
					
					// Re-scan after slider plugin has finished initializing
					setTimeout(function() {
						swiper.slides.forEach(function(slide) {
							initializeSlidersInSlide(slide);
						});
					}, 500);
				}
			});

			// Check if carousel is already initialized (we attached listener too late)
			// Swiper web component has a 'swiper' property when initialized
			if (carousel.swiper) {
				// Already initialized, manually trigger initialization
				injectSwiperShadowStyles();
				
				// Adjust slides per view if needed
				adjustSlidesPerView(carousel.swiper, carousel);
				
				const slides = carousel.querySelectorAll('swiper-slide');
				slides.forEach(function(slide) {
					initializeSlidersInSlide(slide);
				});
			}
		});
	}

	// Wait for DOM and jQuery to be ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function() {
			setTimeout(setupCarouselHandlers, 100);
			// Start watching for style changes
			setTimeout(function() {
				watchCarouselSliders();
				// Run an immediate scan after watchers are set
				scanAndFixCarouselSliders();
			}, 200);
			// Aggressive scanning - check every 100ms for first 3 seconds
			let scanCount = 0;
			const scanInterval = setInterval(function() {
				scanAndFixCarouselSliders();
				scanCount++;
				if (scanCount >= 30) { // 30 * 100ms = 3 seconds
					clearInterval(scanInterval);
				}
			}, 100);
		});
	} else {
		setTimeout(setupCarouselHandlers, 100);
		setTimeout(function() {
			watchCarouselSliders();
			scanAndFixCarouselSliders();
		}, 200);
		let scanCount = 0;
		const scanInterval = setInterval(function() {
			scanAndFixCarouselSliders();
			scanCount++;
			if (scanCount >= 30) {
				clearInterval(scanInterval);
			}
		}, 100);
	}

	// Also run on window load for safety - everything should be ready
	window.addEventListener('load', function() {
		setTimeout(setupCarouselHandlers, 100);
		// Re-watch in case new elements were added
		setTimeout(function() {
			watchCarouselSliders();
			// Final aggressive scan after everything loaded
			scanAndFixCarouselSliders();
		}, 200);
		// One more aggressive scan cycle after load
		let scanCount = 0;
		const scanInterval = setInterval(function() {
			scanAndFixCarouselSliders();
			scanCount++;
			if (scanCount >= 20) { // 2 seconds after load
				clearInterval(scanInterval);
			}
		}, 100);
	});

	// Handle window resize - slide widths may change
	let resizeTimeout;
	window.addEventListener('resize', function() {
		// Debounce resize events
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(function() {
			// Force recalculate all carousel sliders on resize
			const carousels = document.querySelectorAll('swiper-container.bais-carousel');
			carousels.forEach(function(carousel) {
				const sliders = carousel.querySelectorAll('.coca-bais-container, .coca-bais-triple-container');
				sliders.forEach(function(slider) {
					// Clear the cached dimensions to force recalculation
					slider.removeAttribute('data-carousel-width');
					slider.removeAttribute('data-carousel-height');
					reinitializeSlider(slider, true);
				});
			});
		}, 150);
	});

	// Expose for manual triggering if needed
	window.baisCarousel = {
		version: '2.5.0',
		reinitSlider: reinitializeSlider,
		init: setupCarouselHandlers,
		scanAndFix: scanAndFixCarouselSliders,
		watch: watchCarouselSliders,
	};
})();
